/*
 * =========================================================
 * CHEMARRY PET SNACKS v2.1
 * Shopify Dawn 15.4.1
 * =========================================================
 *
 * Controla:
 * - Filtros
 * - Búsqueda
 * - Contador
 * - Selector gatos/perros
 * - Carrusel móvil
 * - Variantes
 * - Precios
 * - Agregar al carrito
 * - Cart drawer de Dawn
 */

if (!customElements.get('chemarry-pet-snacks')) {
  class ChemarryPetSnacks extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.activeAudience = 'all';
      this.activeFilter = 'all';
      this.searchTerm = '';
      this.toastTimer = null;
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }

      window.clearTimeout(this.toastTimer);
    }

    initialize() {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.sectionRoot =
        this.querySelector('[data-pet-snacks-section]') || this;

      this.grid = this.querySelector('[data-snacks-grid]');
      this.cards = Array.from(
        this.querySelectorAll('[data-snacks-card]')
      );

      this.audienceButtons = Array.from(
        this.querySelectorAll('[data-snacks-audience]')
      );

      this.filterButtons = Array.from(
        this.querySelectorAll('[data-snacks-filter]')
      );

      this.searchInput = this.querySelector('[data-snacks-search]');
      this.countElement = this.querySelector('[data-snacks-count]');
      this.emptyElement = this.querySelector('[data-snacks-empty]');
      this.previousButton = this.querySelector('[data-snacks-previous]');
      this.nextButton = this.querySelector('[data-snacks-next]');
      this.toast = this.querySelector('[data-snacks-toast]');
      this.toastText = this.querySelector('[data-snacks-toast-text]');

      this.countSingular =
        this.dataset.countSingular || 'producto';

      this.countPlural =
        this.dataset.countPlural || 'productos';

      this.moneyFormat =
        this.dataset.moneyFormat || '${{amount}}';

      this.currency =
        this.dataset.currency || 'CLP';

      this.activeAudience = this.normalizeAudience(
        this.dataset.defaultAudience || 'all'
      );

      this.activeFilter = 'all';
      this.searchTerm = '';

      this.bindAudienceEvents();
      this.bindFilterEvents();
      this.bindSearchEvents();
      this.bindCarouselEvents();
      this.bindVariantEvents();
      this.bindAddEvents();
      this.bindThemeEditorEvents();

      this.updateAudienceButtons();
      this.updateFilterButtons();
      this.updateProducts();

      requestAnimationFrame(() => {
        this.updateArrowStates();
      });
    }

    /* =====================================================
       1. NORMALIZACIÓN
       ===================================================== */

    normalizeAudience(value) {
      const normalized = String(value || '')
        .trim()
        .toLowerCase();

      if (
        normalized === 'cat' ||
        normalized === 'cats' ||
        normalized === 'gato' ||
        normalized === 'gatos'
      ) {
        return 'cat';
      }

      if (
        normalized === 'dog' ||
        normalized === 'dogs' ||
        normalized === 'perro' ||
        normalized === 'perros'
      ) {
        return 'dog';
      }

      if (
        normalized === 'both' ||
        normalized === 'mixed' ||
        normalized === 'all' ||
        normalized === 'todos'
      ) {
        return 'all';
      }

      return 'all';
    }

    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    }

    normalizeCategory(value) {
      return this.normalizeText(value)
        .replace(/&/g, 'and')
        .replace(/\s+/g, '-');
    }

    /* =====================================================
       2. SELECTOR DE MASCOTA
       ===================================================== */

    bindAudienceEvents() {
      this.audienceButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.activeAudience = this.normalizeAudience(
              button.dataset.snacksAudience
            );

            this.updateAudienceButtons();
            this.updateProducts(true);
          },
          { signal: this.signal }
        );
      });
    }

    updateAudienceButtons() {
      this.audienceButtons.forEach((button) => {
        const buttonAudience = this.normalizeAudience(
          button.dataset.snacksAudience
        );

        const isActive =
          buttonAudience === this.activeAudience;

        button.classList.toggle('is-active', isActive);
        button.setAttribute(
          'aria-selected',
          isActive ? 'true' : 'false'
        );
      });
    }

    /* =====================================================
       3. FILTROS
       ===================================================== */

    bindFilterEvents() {
      this.filterButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.activeFilter = this.normalizeCategory(
              button.dataset.snacksFilter || 'all'
            );

            this.updateFilterButtons();
            this.updateProducts(true);
          },
          { signal: this.signal }
        );
      });
    }

    updateFilterButtons() {
      this.filterButtons.forEach((button) => {
        const buttonFilter = this.normalizeCategory(
          button.dataset.snacksFilter || 'all'
        );

        const isActive =
          buttonFilter === this.activeFilter;

        button.classList.toggle('is-active', isActive);
        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });
    }

    /* =====================================================
       4. BÚSQUEDA
       ===================================================== */

    bindSearchEvents() {
      if (!this.searchInput) return;

      this.searchInput.addEventListener(
        'input',
        () => {
          this.searchTerm = this.normalizeText(
            this.searchInput.value
          );

          this.updateProducts(true);
        },
        { signal: this.signal }
      );

      this.searchInput.addEventListener(
        'search',
        () => {
          this.searchTerm = this.normalizeText(
            this.searchInput.value
          );

          this.updateProducts(true);
        },
        { signal: this.signal }
      );
    }

    /* =====================================================
       5. VISIBILIDAD Y CONTADOR
       ===================================================== */

    cardMatchesAudience(card) {
      if (this.activeAudience === 'all') {
        return true;
      }

      const cardAudience = this.normalizeAudience(
        card.dataset.snacksCardAudience || 'all'
      );

      return (
        cardAudience === 'all' ||
        cardAudience === this.activeAudience
      );
    }

    cardMatchesFilter(card) {
      if (
        this.activeFilter === 'all' ||
        this.activeFilter === ''
      ) {
        return true;
      }

      const categories = String(
        card.dataset.snacksCardCategories || ''
      )
        .split(/\s+/)
        .map((category) =>
          this.normalizeCategory(category)
        )
        .filter(Boolean);

      return categories.includes(this.activeFilter);
    }

    cardMatchesSearch(card) {
      if (!this.searchTerm) {
        return true;
      }

      const searchableContent = this.normalizeText(
        card.dataset.snacksCardSearch ||
          card.textContent ||
          ''
      );

      return searchableContent.includes(this.searchTerm);
    }

    updateProducts(resetScroll = false) {
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const shouldShow =
          this.cardMatchesAudience(card) &&
          this.cardMatchesFilter(card) &&
          this.cardMatchesSearch(card);

        card.hidden = !shouldShow;
        card.setAttribute(
          'aria-hidden',
          shouldShow ? 'false' : 'true'
        );

        if (shouldShow) {
          visibleCount += 1;
        }
      });

      this.updateCount(visibleCount);
      this.updateEmptyState(visibleCount);

      if (resetScroll && this.grid) {
        this.grid.scrollTo({
          left: 0,
          behavior: this.prefersReducedMotion()
            ? 'auto'
            : 'smooth'
        });
      }

      requestAnimationFrame(() => {
        this.updateArrowStates();
      });
    }

    updateCount(count) {
      if (!this.countElement) return;

      const label =
        count === 1
          ? this.countSingular
          : this.countPlural;

      this.countElement.textContent = `${count} ${label}`;
    }

    updateEmptyState(count) {
      if (!this.emptyElement) return;

      this.emptyElement.hidden = count > 0;
    }

    getVisibleCards() {
      return this.cards.filter((card) => !card.hidden);
    }

    /* =====================================================
       6. CARRUSEL
       ===================================================== */

    bindCarouselEvents() {
      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          () => this.moveCarousel(-1),
          { signal: this.signal }
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          () => this.moveCarousel(1),
          { signal: this.signal }
        );
      }

      if (this.grid) {
        this.grid.addEventListener(
          'scroll',
          () => {
            window.requestAnimationFrame(() => {
              this.updateArrowStates();
            });
          },
          {
            signal: this.signal,
            passive: true
          }
        );
      }

      window.addEventListener(
        'resize',
        () => {
          window.requestAnimationFrame(() => {
            this.updateArrowStates();
          });
        },
        {
          signal: this.signal,
          passive: true
        }
      );
    }

    moveCarousel(direction) {
      if (!this.grid) return;

      const visibleCards = this.getVisibleCards();
      const firstCard = visibleCards[0];

      if (!firstCard) return;

      const gridStyles = window.getComputedStyle(this.grid);

      const gap =
        parseFloat(gridStyles.columnGap) ||
        parseFloat(gridStyles.gap) ||
        16;

      const movement =
        firstCard.getBoundingClientRect().width + gap;

      this.grid.scrollBy({
        left: movement * direction,
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });
    }

    updateArrowStates() {
      if (!this.grid) return;

      const visibleCards = this.getVisibleCards();

      const hasOverflow =
        this.grid.scrollWidth >
        this.grid.clientWidth + 2;

      const atStart = this.grid.scrollLeft <= 2;

      const atEnd =
        this.grid.scrollLeft +
          this.grid.clientWidth >=
        this.grid.scrollWidth - 2;

      if (this.previousButton) {
        this.previousButton.disabled =
          !hasOverflow ||
          atStart ||
          visibleCards.length === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled =
          !hasOverflow ||
          atEnd ||
          visibleCards.length === 0;
      }
    }

    /* =====================================================
       7. VARIANTES Y PRECIOS
       ===================================================== */

    bindVariantEvents() {
      const selectors = Array.from(
        this.querySelectorAll('[data-snacks-variant]')
      );

      selectors.forEach((selector) => {
        selector.addEventListener(
          'change',
          () => {
            this.updateVariant(selector);
          },
          { signal: this.signal }
        );

        this.updateVariant(selector);
      });
    }

    updateVariant(selector) {
      const card = selector.closest(
        '[data-snacks-card]'
      );

      if (!card) return;

      const selectedOption =
        selector.options[selector.selectedIndex];

      if (!selectedOption) return;

      const variantId = selectedOption.value;
      const available =
        selectedOption.dataset.available === 'true';

      const price = Number(
        selectedOption.dataset.price || 0
      );

      const comparePrice = Number(
        selectedOption.dataset.comparePrice || 0
      );

      const addButton = card.querySelector(
        '[data-snacks-add]'
      );

      const buttonText = card.querySelector(
        '[data-snacks-button-text]'
      );

      const currentPrice = card.querySelector(
        '[data-snacks-price-current]'
      );

      const comparePriceElement = card.querySelector(
        '[data-snacks-price-compare]'
      );

      const availabilityElement = card.querySelector(
        '[data-snacks-availability]'
      );

      if (addButton) {
        addButton.dataset.variantId = variantId;
        addButton.disabled = !available;

        const availableText =
          addButton.dataset.availableText ||
          'Agregar al carrito';

        const soldOutText =
          addButton.dataset.soldOutText ||
          'Agotado';

        if (buttonText) {
          buttonText.textContent = available
            ? availableText
            : soldOutText;
        }
      }

      if (currentPrice) {
        currentPrice.textContent =
          this.formatMoney(price);
      }

      if (comparePriceElement) {
        const hasDiscount =
          comparePrice > price && comparePrice > 0;

        comparePriceElement.classList.toggle(
          'is-hidden',
          !hasDiscount
        );

        comparePriceElement.textContent =
          hasDiscount
            ? this.formatMoney(comparePrice)
            : '';
      }

      if (availabilityElement) {
        const soldOutText =
          addButton?.dataset.soldOutText ||
          'Agotado';

        const availableText =
          availabilityElement.dataset.availableText ||
          'Disponible';

        availabilityElement.classList.toggle(
          'is-unavailable',
          !available
        );

        const textNodes = Array.from(
          availabilityElement.childNodes
        ).filter(
          (node) => node.nodeType === Node.TEXT_NODE
        );

        if (textNodes.length > 0) {
          textNodes[textNodes.length - 1].textContent =
            available
              ? ` ${availableText}`
              : ` ${soldOutText}`;
        }
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      if (window.Shopify?.formatMoney) {
        try {
          return window.Shopify.formatMoney(
            cents,
            this.moneyFormat
          );
        } catch (error) {
          // Continúa con Intl.
        }
      }

      try {
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: this.currency || 'CLP',
          maximumFractionDigits:
            this.currency === 'CLP' ? 0 : 2
        }).format(amount);
      } catch (error) {
        return `$${Math.round(amount).toLocaleString(
          'es-CL'
        )}`;
      }
    }

    /* =====================================================
       8. AGREGAR AL CARRITO
       ===================================================== */

    bindAddEvents() {
      const addButtons = Array.from(
        this.querySelectorAll('[data-snacks-add]')
      );

      addButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.addToCart(button),
          { signal: this.signal }
        );
      });
    }

    async addToCart(button) {
      if (
        button.disabled ||
        button.classList.contains('is-loading')
      ) {
        return;
      }

      const variantId = Number(
        button.dataset.variantId
      );

      const quantity = Math.max(
        1,
        Number(button.dataset.quantity || 1)
      );

      if (!variantId) {
        this.showToast(
          'Selecciona una opción disponible.',
          true
        );
        return;
      }

      const cartDrawer =
        document.querySelector('cart-drawer');

      const cartNotification =
        document.querySelector('cart-notification');

      const cartComponent =
        cartDrawer || cartNotification;

      const sections =
        cartComponent?.getSectionsToRender
          ? cartComponent
              .getSectionsToRender()
              .map((section) => section.id)
          : [
              'cart-drawer',
              'cart-icon-bubble'
            ];

      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity
                }
              ],
              sections,
              sections_url:
                window.location.pathname
            })
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              'No fue posible agregar el producto.'
          );
        }

        const successMessage =
          button.dataset.successMessage ||
          'Producto agregado al carrito.';

        this.showToast(successMessage, false);
        this.refreshCart(result, cartComponent);
      } catch (error) {
        this.showToast(
          error.message ||
            'No fue posible agregar el producto.',
          true
        );
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    refreshCart(result, cartComponent) {
      if (
        cartComponent &&
        typeof cartComponent.renderContents ===
          'function'
      ) {
        cartComponent.renderContents(result);

        window.setTimeout(() => {
          if (
            typeof cartComponent.open === 'function'
          ) {
            cartComponent.open();
          }
        }, 80);

        return;
      }

      this.refreshCartBubble(result);

      const cartIcon = document.querySelector(
        '#cart-icon-bubble'
      );

      if (cartIcon) {
        cartIcon.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true
          })
        );
      }
    }

    refreshCartBubble(result) {
      const bubbleSection =
        result.sections?.['cart-icon-bubble'];

      if (!bubbleSection) return;

      const temporaryContainer =
        document.createElement('div');

      temporaryContainer.innerHTML =
        bubbleSection;

      const newBubble =
        temporaryContainer.querySelector(
          '#cart-icon-bubble'
        );

      const currentBubble =
        document.querySelector(
          '#cart-icon-bubble'
        );

      if (newBubble && currentBubble) {
        currentBubble.innerHTML =
          newBubble.innerHTML;
      }
    }

    /* =====================================================
       9. NOTIFICACIONES
       ===================================================== */

    showToast(message, isError = false) {
      if (!this.toast || !this.toastText) return;

      window.clearTimeout(this.toastTimer);

      this.toastText.textContent = message;
      this.toast.hidden = false;
      this.toast.classList.toggle(
        'is-error',
        isError
      );

      this.toastTimer = window.setTimeout(() => {
        this.toast.hidden = true;
      }, isError ? 5000 : 3200);
    }

    /* =====================================================
       10. EDITOR DE SHOPIFY
       ===================================================== */

    bindThemeEditorEvents() {
      if (!window.Shopify?.designMode) return;

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const selectedCard =
            event.target.closest?.(
              '[data-snacks-card]'
            );

          if (!selectedCard) return;

          this.activeAudience = 'all';
          this.activeFilter = 'all';
          this.searchTerm = '';

          if (this.searchInput) {
            this.searchInput.value = '';
          }

          this.updateAudienceButtons();
          this.updateFilterButtons();
          this.updateProducts();

          window.setTimeout(() => {
            selectedCard.scrollIntoView({
              behavior: this.prefersReducedMotion()
                ? 'auto'
                : 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }, 50);
        },
        { signal: this.signal }
      );
    }

    /* =====================================================
       11. ACCESIBILIDAD
       ===================================================== */

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }
  }

  customElements.define(
    'chemarry-pet-snacks',
    ChemarryPetSnacks
  );
}

/*
 * Shopify puede recargar una sección sin recargar la página.
 * Esta escucha asegura que la instancia nueva se inicialice
 * correctamente dentro del editor.
 */

document.addEventListener(
  'shopify:section:load',
  (event) => {
    const sections = event.target.querySelectorAll(
      'chemarry-pet-snacks'
    );

    sections.forEach((section) => {
      if (typeof section.initialize === 'function') {
        section.initialize();
      }
    });
  }
);